import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { getMaxPointSize } from '../scene-manager.js';

// Shared-rig contract (see the framing addendum). 'forbidden': same as
// resonance.js, its own sibling in this file's lineage - analytic light only.
const RIG = 'forbidden';
export { RIG };

// Orchestrator registry entry (see visualizer-registry.js).
export const PROFILE = {
  id: 'sand',
  family: 'organic',
  energyLevel: 0.25,
  bpmMin: 40,
  bpmMax: 140,
  movement: 0.30,
  danceability: 0.30,
  tonality: 0.75,
  prefers: { dynamics: [0.35, 1.0], percussiveness: [0.0, 0.60] },
  enabled: true
};

// Sand: falling sand that piles up and avalanches once its own slope gets too
// steep. Second in the physical vein after resonance.js - what this
// one adds is that the material ACCUMULATES. Resonance always
// return to rest; here the pile grows for the whole track and nothing ever
// cancels out. Avalanches punctuate the image on their own terms - a system
// at the edge of collapse, never a metronome - which is exactly why
// director.pulse() is never touched (see update()).
//
// MODEL: a HEIGHTFIELD of columns, not particles. Simulating individual
// grains would be both too expensive and unreadable at this scale; a single
// Float32Array of column heights, relaxed toward the angle of repose every
// fixed step, is what actually reads as sand from a normal viewing distance
// - see relaxPair below, the heart of the whole file.
//
// RENDERING is deliberately NOT one quad per column. A hard-edged block per
// column reads as a staircase of visible steps no matter how many columns
// there are - at 220 columns on a 2560px-wide frame a step is still ~12px,
// plainly visible. Instead the pile is a single continuous ribbon (a
// triangle strip): one vertex per column at a SMOOTHED height (a weighted
// average of that column and its two neighbours), with the GPU linearly
// interpolating between consecutive vertices. The simulation itself still
// runs on the raw, unsmoothed heightfield - smoothing is a render-only
// step, applied once per frame, never fed back into the physics.

// --- columns -------------------------------------------------------------
const MAX_COLUMNS = 220; // tier-2 ceiling; the one column-height array is sized to this ONCE, at module load
const ANGLE_OF_REPOSE_DEG = 34; // dry sand
const REPOSE_JITTER_FRAC = 0.08; // +-8% per column, drawn once at init - see slopeJitter below
const RELAX_ITERATIONS = 4; // one pass leaves visibly-too-steep slopes; see _simulateStep
// Gaussian-ish spread over 6 columns, offsets col0-2..col0+3 (binomial
// row for n=5, normalised) - wide enough that a single deposit rounds the
// peak instead of spiking it. A 3-tap kernel (the original width) left a
// mathematically sharp apex; this is what makes the summit ARRIVE rounded
// rather than being rounded off after the fact by relaxation alone.
const DEPOSIT_WEIGHTS = [1 / 32, 5 / 32, 10 / 32, 10 / 32, 5 / 32, 1 / 32];
const DEPOSIT_OFFSET0 = -2; // first tap is col0 + DEPOSIT_OFFSET0
// Deposit lands within +-this many columns of the emission point, redrawn
// every simulation step - without it every grain falls at the exact same
// spot and the summit is a mathematical point, not a pile.
const DEPOSIT_JITTER_COLUMNS = 2;

// --- world scale -----------------------------------------------------------
const CAMERA_DIST = 9.0;
// "88% de la hauteur visible": measured against the FULL frustum height,
// not a reduced reference. A 34-degree pile is geometrically incapable of
// reaching anywhere near that on its own (it would need a base wider than
// the frame itself - half-width * tan(34) tops out around 60% of
// worldHeight before the base runs out of room), so in practice the
// sinking floor is a backstop that rarely has to act, not the mechanism
// that shapes the pile's everyday size. That is the point: the pile's own
// physics keeps it in frame; the sink only steps in if it doesn't.
const SINK_THRESHOLD_FRAC = 0.88;

// --- the pile it starts with -----------------------------------------------
// The heights used to begin at zero and the pile grew from nothing, which is
// right for the arc of a track but wrong for the moment you switch into it:
// measured, the frame is under 2% covered after ten seconds and under 6% after
// thirty, so what you actually get is a black screen with a thread of falling
// grains for the better part of half a minute. On a wall display nobody waits
// that out.
//
// So it opens on a pile that is already there, as a fraction of the frame
// height. 0.20 is about where free accumulation gets to on its own after
// twenty-odd seconds - unmistakably a heap with a crest, and still only a
// third of the way to the geometric ceiling, so the whole growth arc the file
// is built around is left intact.
const SEED_HEIGHT_FRAC = 0.20;

// --- deposit / emission ------------------------------------------------
const EMISSION_DRIFT_PERIOD_S = 45;
// Fraction of the half-width the emission point wanders across. Kept
// modest on purpose: the drift's job is to make WHERE sand falls feel
// alive, not to smear deposits across the whole array - a tall peak needs
// a base of roughly 2*H/(tan(repose)*dx) columns, and a drift sweeping
// anywhere near that same range never lets material concentrate enough at
// any one spot to build one. Confirmed directly: at 0.62 the point swept
// ~137 of 220 columns per half-cycle, almost the entire array, and 30s of
// accumulation produced a wide, ankle-height ridge instead of a pile.
const EMISSION_DRIFT_FRAC = 0.14;
// world-height-units/s at neutral bands.low. Tuned empirically, not
// analytically: the naive "triangular cone volume = H^2/tan(repose)"
// prediction undershot the REAL relationship several-fold once the
// emission drift and thousands of relaxation passes were both in play -
// measured directly instead, then scaled to reach a substantial pile in
// roughly a couple of minutes at neutral reactivity, not tens of minutes.
const FLOW_RATE_BASE = 2.6;
const FLOW_BAND_MIN_MUL = 0.35;
const FLOW_BAND_MAX_MUL = 2.2;
const DYNAMICS_SWING_MIN = 0.6;
const DYNAMICS_SWING_MAX = 1.4;

// --- sinking floor -------------------------------------------------------
// "Plus elegant qu'une remise a zero": once the pile presses the ceiling,
// every column shrinks by a constant FACTOR per second (exponential decay,
// applied every fixed substep - see _simulateStep) rather than snapping
// back to zero. The pile is therefore always in frame and accumulation is
// unbounded.
const BASE_SINK_RATE_PER_S = 0.045;
const SINK_RATE_PROGRESS_MIN_MUL = 0.35; // slowest sinking, tallest pile - "culmine au coeur du morceau"
const SINK_RATE_PROGRESS_MAX_MUL = 1.6;  // fastest sinking - low at the very start/end
const TRACK_CHANGE_SINK_DURATION_S = 3;
const TRACK_CHANGE_SINK_RATE_PER_S = 0.75; // (1-0.75)^3 ~= 0.016 - "s'enfonce presque entierement" in 3s

// --- fixed-step integration ------------------------------------------------
// Same reasoning as resonance.js's own: a variable deltaTime makes the flow
// rate framerate-dependent, and an avalanche relaxation double-applied on a
// long frame can visibly overshoot.
const DT_CLAMP = 0.05;
const FIXED_DT = 1 / 60;
const MAX_SUBSTEPS = 3;

// --- the falling net -----------------------------------------------------
const NET_FALL_SPEED = 3.4; // world units/s
const NET_DISPERSION_BASE = 0.24;
const NET_DISPERSION_BAND_SWING = 0.6; // bands.mid
const NET_POINT_PX = 2.2;
// A point disappears when it reaches the pile's ACTUAL (smoothed, i.e.
// rendered) surface at its own dispersed x, not a single shared reference
// sampled at the emission column - see uHeightTex. This is a headroom cap
// for the 8-bit encoding, comfortably above anything the pile can
// physically reach (see SINK_THRESHOLD_FRAC's own comment on the
// ~60%-of-worldHeight geometric ceiling), not a real limit on pile height.
const NET_HEIGHT_TEX_MAX = 9.0;

// --- render -----------------------------------------------------------
const CREST_PX = 2.0; // thickness of the crest ribbon, in screen pixels
// The pile's own body colour. 0x2e2a26 first, and it was too dark to be a
// colour at all: measured off the render, the body came out at RGB(18,15,12) -
// a displayed luminance of 0.060 against a background at 0.007. On a monitor
// in a dark room that is a just-visible shape; on a television it is nothing,
// because a TV's black-level handling crushes everything under roughly 16/255.
// That is the report this was found from ("on ne voit pas du tout le tas de
// sable"): only the crest (0.90) and the falling grains survived, so the pile
// read as an outline drawn around a hole.
//
// 0x6b6157 measures at RGB(77,66,56), displayed luminance 0.266 - about five
// times any TV's crush point, so there is no plausible picture setting where
// it disappears.
//
// It is NOT the brightest value available. That is 0x8a7d6e (0.212 linear):
// the slope facing away from the emitter is boosted by LIT_FACE_BOOST (+55%),
// so LUMINANCE_CAP_SHADER is reached at 0.34/1.55 = 0.219, and anything past
// it has its lit face scaled back by the cap - 0xa89880 loses 32% of it, which
// silently flattens the directional shading this file works to produce: the
// pile gets brighter and LESS legible. Compared side by side at equal pile
// height, 0x8a7d6e reads as pale grey and gives up the warm sand tone, while
// the crest's separation above the body is the same either way (+0.31
// displayed at both). So the ceiling is known and documented, and this sits
// below it on purpose.
//
// The cover tint cannot undo any of this: at PALETTE_TINT_AMOUNT 0.20 even a
// pure black dominant only pulls the body back to 0.099 linear, still four
// times the value this replaces.
const PILE_COLOR = new THREE.Color(0x6b6157);
const PALETTE_TINT_AMOUNT = 0.20;
const FALLBACK_VIVID = 0xd9b877;
const CREST_LUMINANCE = 0.90;
const CREST_BAND_MIN_MUL = 0.7;  // bands.high
const CREST_BAND_MAX_MUL = 1.5;
const LIT_FACE_BOOST = 0.55; // the slope facing away from the emission point

// Background - deep black, a hint of gradient, fine grain, dithered to kill
// banding (same reasoning as constellation.js's own background).
const BG_TOP_COLOR = new THREE.Color(0x08090c);
const BG_BOTTOM_COLOR = new THREE.Color(0x000000);

const LUMINANCE_CAP_SHADER = 0.34; // shader-space target; see sumi.js/murmuration.js for why this is tuned against real pixels, not the 0.74 spec ceiling directly

// --- no-analysis fallback --------------------------------------------------
const FALLBACK_LFO_PERIOD_MIN_S = 9;
const FALLBACK_LFO_PERIOD_MAX_S = 15;

const TIER_LAYOUT = {
  0: { columns: 140, netPoints: 60, slopeShading: false },
  1: { columns: 180, netPoints: 90, slopeShading: true },
  2: { columns: 220, netPoints: 120, slopeShading: true }
};
const DEFAULT_TIER = 1;

function clamp01(x) {
  return Math.max(0, Math.min(1, x));
}
function lerp(a, b, t) {
  return a + (b - a) * t;
}
function urlNumber(name) {
  if (typeof location === 'undefined') return NaN;
  return parseFloat(new URLSearchParams(location.search).get(name));
}

// Own local copy of the safe-zone chunk, floor 0.35 - the shared chunk
// bakes in 0.32 (see shaders/safeZone.glsl.js), and every visualizer with a
// different floor keeps its own copy rather than trying to parametrise the
// shared one (sumi.js's 0.40, diorama.js's 0.45, pendulum.js's 0.35 - same
// pattern). Vertex-space: the pile ribbon and crest ribbon are each made of
// many real vertices (one pair per column), and the net is 120 points, not
// a single sparse full-frame quad, so interpolating a per-vertex
// attenuation across them is accurate.
const SAFE_ZONE_FLOOR = 0.35;
const safeZoneChunk = `
  uniform float uAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  float safeZoneAttenuation(vec4 clipPos) {
    vec2 ndc = clipPos.xy / max(clipPos.w, 0.0001);
    vec2 s = vec2(ndc.x * uAspect, ndc.y);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    return mix(${SAFE_ZONE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, d));
  }
`;

// --- module-level pre-allocated simulation state ---------------------------
// One pile is ever active at a time (SceneManager owns a single
// activeVisualizer), so a module-level singleton is safe - init() only
// resets values into these, update() never allocates.
const height = new Float32Array(MAX_COLUMNS);
// Per-column angle-of-repose multiplier, +-REPOSE_JITTER_FRAC, drawn once
// at init and fixed for the visualizer's whole lifetime (see
// SandVisualizer.init) - without it every flank relaxes toward the exact
// same slope everywhere and reads as a drafted line, not a heap of grains.
const slopeJitter = new Float32Array(MAX_COLUMNS);
// Render-only, recomputed once per frame from height[] via computeSlopes -
// central difference, feeds the lit-face shading. Never read back into the
// simulation. (An earlier version of this file also rendered from a
// SMOOTHED copy of height[] - a 3-tap neighbour average - to help hide the
// per-column steps of the old quad-based renderer. Now that the pile is a
// continuous ribbon, that smoothing is not just unnecessary but actively
// harmful: it damped the +-8% repose jitter and the deposit/profile noise
// by ~40% before they ever reached the screen, which is exactly why the
// pile started reading as a smooth, geometric triangle again. The ribbon's
// own linear interpolation between RAW per-column vertices is already
// continuous - smoothing was never what removed the steps.)
const slope = new Float32Array(MAX_COLUMNS);

// --- simulation (pure functions over the module-level arrays) --------------
function depositGaussian(columnCount, colF, amount) {
  if (amount <= 0) return;
  const col0 = Math.round(colF);
  for (let k = 0; k < DEPOSIT_WEIGHTS.length; k++) {
    const idx = col0 + DEPOSIT_OFFSET0 + k;
    if (idx < 0 || idx >= columnCount) continue;
    height[idx] += amount * DEPOSIT_WEIGHTS[k];
  }
}

// One symmetric relaxation pass over every adjacent pair, in the given
// direction. The formula is applied in BOTH signs (a column can be too high
// relative to either neighbour) - "compare sa hauteur a ses deux voisines".
// The direction itself (see _simulateStep) alternates from one SIMULATION
// STEP to the next, not within these 4 iterations - that is what keeps a
// cascade from always resolving preferentially toward one side (AC3).
function relaxOnce(columnCount, maxSlope, forward) {
  if (forward) {
    for (let i = 0; i < columnCount - 1; i++) relaxPair(i, maxSlope);
  } else {
    for (let i = columnCount - 2; i >= 0; i--) relaxPair(i, maxSlope);
  }
}
function relaxPair(i, maxSlope) {
  // Which branch fires depends only on the SIGN of diff (which side is
  // higher right now), not on column parity or sweep order, so there is no
  // systematic left/right bias (AC3) even though each branch reads only
  // ONE column's own jitter. Averaging the pair's two jitters instead (an
  // earlier version of this) diluted the intended +-8% swing toward the
  // mean - measured directly, it left the flanks visibly straight after
  // the thousands of relaxation passes a real pile accumulates. Reading
  // the higher column's own angle is also the more physical reading: the
  // material actually sitting there is what determines how steep IT can
  // stand.
  const diff = height[i] - height[i + 1];
  if (diff > 0) {
    const localMax = maxSlope * slopeJitter[i];
    if (diff > localMax) {
      const move = (diff - localMax) * 0.5;
      height[i] -= move;
      height[i + 1] += move;
    }
  } else if (diff < 0) {
    const localMax = maxSlope * slopeJitter[i + 1];
    if (-diff > localMax) {
      const move = (-diff - localMax) * 0.5;
      height[i + 1] -= move;
      height[i] += move;
    }
  }
}

function computeMaxHeight(columnCount) {
  let m = 0;
  for (let i = 0; i < columnCount; i++) if (height[i] > m) m = height[i];
  return m;
}

// Render-only: central-difference slope of the RAW heightfield, feeding
// the lit-face shading. The ribbon's vertices themselves carry height[i]
// directly (see _writeRenderAttributes) - the GPU's own linear
// interpolation between adjacent vertices is what makes the profile
// continuous, not any pre-smoothing of the data.
function computeSlopes(columnCount, dx) {
  for (let i = 0; i < columnCount; i++) {
    const left = i > 0 ? height[i - 1] : height[i];
    const right = i < columnCount - 1 ? height[i + 1] : height[i];
    slope[i] = (left - right) / (2 * dx);
  }
}

// --- shaders -----------------------------------------------------------
// Both the pile ribbon and the crest ribbon share the same topology: two
// vertices per column (a base vertex and a top/crest vertex), triangulated
// into a strip. Building that index buffer once and reusing it for both
// geometries keeps the two ribbons from drifting apart.
function buildRibbonIndices(columnCount) {
  const indices = [];
  for (let i = 0; i < columnCount - 1; i++) {
    const b0 = 2 * i, t0 = 2 * i + 1, b1 = 2 * (i + 1), t1 = 2 * (i + 1) + 1;
    indices.push(b0, t0, t1);
    indices.push(b0, t1, b1);
  }
  return indices;
}

function pileVertexShader() {
  return `
    precision mediump float;
    attribute float aLocalY; // 0 at the ribbon's base edge, 1 at its top (crest) edge
    attribute float aSlope;

    varying float vLocalY;
    varying float vSlope;

    ${safeZoneChunk}

    void main() {
      vLocalY = aLocalY;
      vSlope = aSlope;
      vec4 clip = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      gl_Position = clip;
    }
  `;
}

function pileFragmentShader() {
  return `
    precision mediump float;
    varying float vLocalY;
    varying float vSlope;

    uniform vec3 uPileColor;
    uniform float uEmissionSideSign;
    uniform float uSlopeShading;  // 0 or 1, tier switch

    float hash(vec2 p) {
      return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
    }

    void main() {
      // Compacted look: darker toward the base.
      vec3 col = uPileColor * mix(0.72, 1.0, vLocalY);

      // The face pointing AWAY from the emission point catches the light -
      // "le versant oppose au point d'emission". uEmissionSideSign is a
      // single global sign (which side of the frame the emitter currently
      // sits on), not a per-vertex geometric normal - simple, and enough
      // to read as directional light rather than a flat coat.
      float lit = clamp(vSlope * -uEmissionSideSign, 0.0, 1.0) * uSlopeShading;
      col *= 1.0 + lit * ${LIT_FACE_BOOST.toFixed(2)};

      // Fine granular texture - cheap hash noise, not a texture read (no
      // grain asset exists for this file, and at this element size a
      // procedural speckle reads the same). Purely a surface texture now -
      // the profile's continuity comes from the geometry itself, not from
      // this.
      float grain = hash(gl_FragCoord.xy * 0.9);
      col *= 0.94 + grain * 0.12;

      float lum = dot(col, vec3(0.299, 0.587, 0.114));
      const float LUM_CAP = ${LUMINANCE_CAP_SHADER.toFixed(3)};
      if (lum > LUM_CAP) col *= LUM_CAP / max(lum, 0.001);

      gl_FragColor = vec4(col, 1.0);
    }
  `;
}

// The crest is real geometry now (a second, thin ribbon riding the exact
// same smoothed height, offset down by a fixed screen-pixel thickness),
// not a fragment-shader fraction of each column's own height - it follows
// a continuous line because its OWN vertices already sit on that line.
function crestVertexShader() {
  return `
    precision mediump float;
    varying float vSafeAtt;
    ${safeZoneChunk}
    void main() {
      vec4 clip = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      gl_Position = clip;
      vSafeAtt = safeZoneAttenuation(clip);
    }
  `;
}

function crestFragmentShader() {
  return `
    precision mediump float;
    varying float vSafeAtt;
    uniform vec3 uCrestColor;
    uniform float uCrestBright;
    void main() {
      vec3 col = uCrestColor * uCrestBright * vSafeAtt;
      float lum = dot(col, vec3(0.299, 0.587, 0.114));
      const float LUM_CAP = ${LUMINANCE_CAP_SHADER.toFixed(3)};
      if (lum > LUM_CAP) col *= LUM_CAP / max(lum, 0.001);
      gl_FragColor = vec4(col, 1.0);
    }
  `;
}

function netVertexShader() {
  return `
    precision mediump float;
    attribute float aSeed;

    uniform float uTime;
    uniform float uCycleDuration;
    uniform float uEmissionX;
    uniform float uEmissionY;
    uniform float uDispersion;
    uniform float uFallSpeed;
    // A point disappears at the pile's REAL (smoothed/rendered) surface
    // under its own dispersed x, not a single height sampled at the
    // emission column - a point that has drifted sideways would otherwise
    // pass through a taller neighbouring stretch of the pile, or vanish
    // early over a lower one, before it ever reaches its own true local
    // surface. uHeightTex is the smoothed column heightfield, one texel
    // per column, updated every frame.
    uniform sampler2D uHeightTex;
    uniform float uHeightTexMax;
    uniform float uWorldHalfWidth;
    uniform float uFloorY;
    // gl_PointSize wants an actual FRAMEBUFFER-pixel size. "desiredPx *
    // refDist / actualDist" is the correct falloff (a real 2.2px AT
    // uRefDist, shrinking with distance beyond it) - see
    // murmuration.js's own header comment for the fuller derivation and
    // for the exact bug this avoids: dividing by a world-per-pixel factor
    // instead of multiplying by a reference distance inverts the
    // relationship and produces a point that GROWS instead of shrinks,
    // by roughly uPixelScale itself (hundreds of pixels) - confirmed
    // directly, the net alone bloomed into a wash covering the entire
    // frame.
    uniform float uRefDist;
    uniform float uDprScale;
    uniform float uPointSizeMax;

    varying float vVisible;
    varying float vSafeAtt;

    ${safeZoneChunk}

    void main() {
      float startOffset = aSeed * uCycleDuration;
      float age = mod(uTime - startOffset, uCycleDuration);
      float y = uEmissionY - uFallSpeed * age;

      // Each point keeps its own fixed dispersion magnitude (not just its
      // own fixed lateral DIRECTION) so the net doesn't fall as a single
      // rigid, uniformly-flaring shape - some grains drift further than
      // others, same as a real stream breaking up as it falls.
      float lateralSeed = fract(aSeed * 37.13) - 0.5;
      float dispMul = 0.55 + fract(aSeed * 53.1) * 0.9;
      float x = uEmissionX + lateralSeed * 2.0 * uDispersion * dispMul * sqrt(age + 0.02);

      float u = clamp((x + uWorldHalfWidth) / (2.0 * uWorldHalfWidth), 0.0, 1.0);
      float pileWorldY = uFloorY + texture2D(uHeightTex, vec2(u, 0.5)).r * uHeightTexMax;
      vVisible = (y > pileWorldY) ? 1.0 : 0.0;

      vec3 world = vec3(x, y, 0.05);
      vec4 mv = modelViewMatrix * vec4(world, 1.0);
      float sizePx = ${NET_POINT_PX.toFixed(1)} * uDprScale;
      gl_PointSize = clamp(sizePx * uRefDist / max(-mv.z, 0.001), 1.0, uPointSizeMax);

      vec4 clip = projectionMatrix * mv;
      gl_Position = clip;
      vSafeAtt = safeZoneAttenuation(clip);
    }
  `;
}

function netFragmentShader() {
  return `
    precision mediump float;
    varying float vVisible;
    varying float vSafeAtt;
    uniform vec3 uNetColor;
    uniform float uNetBright;
    void main() {
      if (vVisible < 0.5) discard;
      vec2 c = gl_PointCoord - 0.5;
      float r = length(c) * 2.0;
      float coverage = 1.0 - smoothstep(0.4, 1.0, r);
      if (coverage <= 0.02) discard;
      vec3 col = uNetColor * uNetBright * coverage * vSafeAtt;
      gl_FragColor = vec4(col, 1.0);
    }
  `;
}

function backgroundVertexShader() {
  return `
    precision mediump float;
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `;
}

function backgroundFragmentShader() {
  return `
    precision mediump float;
    varying vec2 vUv;
    uniform vec3 uTop;
    uniform vec3 uBottom;
    uniform float uTime;

    float hash(vec2 p) {
      return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
    }

    void main() {
      vec3 col = mix(uBottom, uTop, vUv.y);
      float grain = (hash(vUv * vec2(900.0, 620.0) + uTime * 0.6) - 0.5) * 0.025;
      col += grain;
      // Dither - break up quantisation banding on a very dark, near-flat
      // gradient (same reasoning as constellation.js's own background).
      float dither = (hash(gl_FragCoord.xy) - 0.5) / 255.0;
      col += dither;
      gl_FragColor = vec4(max(col, 0.0), 1.0);
    }
  `;
}

export class SandVisualizer extends Visualizer {
  constructor(options) {
    super();
    const forcedTier = urlNumber('sandtier');
    const tier = Number.isFinite(forcedTier) && TIER_LAYOUT[forcedTier] != null
      ? forcedTier
      : (options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER);
    this._tier = TIER_LAYOUT[tier] ? tier : DEFAULT_TIER;

    const forcedSlope = urlNumber('sandslope');
    this._forcedSlopeDeg = Number.isFinite(forcedSlope) ? forcedSlope : null;
    const forcedFlow = urlNumber('sandflow');
    this._forcedFlow = Number.isFinite(forcedFlow) ? forcedFlow : null;
    const params = new URLSearchParams(typeof location !== 'undefined' ? location.search : '');
    this._debugHeight = params.get('sandheight') === '1';
    // ?sandinfo=1 - logs the numbers that actually determine whether the
    // pile's silhouette pixellates: active column count, tier, the
    // computed column pitch in world units, and the visible width at the
    // pile's own plane. Logged once at init and again on every resize,
    // since a stale value from before a resize is exactly the kind of bug
    // this is here to catch.
    this._debugInfo = params.get('sandinfo') === '1';
    // ?sandseed=<fraction of frame height>, 0 for the old grow-from-nothing
    // behaviour - there to compare the two directly.
    const seed = urlNumber('sandseed');
    this._seedFrac = Number.isFinite(seed) && seed >= 0 ? seed : SEED_HEIGHT_FRAC;
  }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    camera.position.set(0, 0, CAMERA_DIST);
    camera.lookAt(0, 0, 0);

    const layout = TIER_LAYOUT[this._tier];
    this._columnCount = layout.columns;
    this._netPointCount = layout.netPoints;
    this._slopeShading = layout.slopeShading;

    this._recomputeWorld();

    height.fill(0, 0, this._columnCount);
    slope.fill(0, 0, this._columnCount);
    for (let i = 0; i < this._columnCount; i++) {
      slopeJitter[i] = 1 + (Math.random() * 2 - 1) * REPOSE_JITTER_FRAC;
    }

    this._time = 0;
    this._accumulator = 0;
    this._sweepForward = true;
    this._paused = false;
    this._lastTrackId = undefined;
    this._trackChangeSinkT = 0; // counts down from TRACK_CHANGE_SINK_DURATION_S after a track change
    this._emissionX = 0;
    this._crestWorldThickness = 0.05;
    // AFTER _emissionX: the seed is centred on the emitter, and reading it
    // before it is assigned puts a NaN through the column index and silently
    // writes nothing at all.
    this._seedPile();

    this._fallbackPeriod = lerp(FALLBACK_LFO_PERIOD_MIN_S, FALLBACK_LFO_PERIOD_MAX_S, Math.random());

    this._lastPaletteRef = undefined;
    this._pileColorFrom = PILE_COLOR.clone();
    this._pileColorTo = PILE_COLOR.clone();
    this._crestColorFrom = new THREE.Color(FALLBACK_VIVID);
    this._crestColorTo = new THREE.Color(FALLBACK_VIVID);
    this._paletteLerpStart = -Infinity;

    this._buildBackground();
    this._buildPile();
    this._buildCrest();
    this._buildNet();
    if (this._debugHeight) this._buildHeightDebug();
    this._logSandInfo();
  }

  // Lays down the pile the visualizer opens with.
  //
  // Built at the ANGLE OF REPOSE, column by column, using each column's own
  // jitter and the same index the relaxation reads for that side (left of the
  // apex, relaxPair compares against slopeJitter[i + 1]; right of it,
  // slopeJitter[i - 1]). That matters: a shape assembled at exactly the slope
  // the physics considers stable is one the relaxation has no work to do on,
  // so it neither slumps nor visibly settles on the first frames. It is the
  // pile this file would have built for itself, just already built.
  _seedPile() {
    const frac = this._seedFrac;
    if (!(frac > 0)) return;
    const repose = THREE.MathUtils.degToRad(ANGLE_OF_REPOSE_DEG);
    const step = Math.tan(repose) * this._dx;
    // A repose cone cannot be taller than its own base allows - half the frame
    // width at 34 degrees, about 60% of the height (see SINK_THRESHOLD_FRAC's
    // note). Clamped so an over-large seed cannot ask for a shape that is not
    // geometrically possible.
    const ceiling = this._worldHalfWidth * Math.tan(repose);
    const apex = Math.min(this._worldHeight * frac, ceiling);
    const c = Math.max(0, Math.min(this._columnCount - 1,
      Math.round((this._emissionX + this._worldHalfWidth) / this._dx - 0.5)));
    height[c] = apex;
    for (let i = c - 1; i >= 0; i--) {
      height[i] = Math.max(0, height[i + 1] - step * slopeJitter[i + 1]);
    }
    for (let i = c + 1; i < this._columnCount; i++) {
      height[i] = Math.max(0, height[i - 1] - step * slopeJitter[i - 1]);
    }
  }

  _recomputeWorld() {
    const fovRad = THREE.MathUtils.degToRad(this._camera.fov);
    this._worldHeight = 2 * CAMERA_DIST * Math.tan(fovRad / 2);
    this._worldHalfWidth = (this._worldHeight * this._camera.aspect) / 2;
    this._floorY = -this._worldHeight / 2;
    this._dx = (2 * this._worldHalfWidth) / this._columnCount;
    this._emissionY = this._floorY + this._worldHeight * 0.92;
  }

  _logSandInfo() {
    if (!this._debugInfo) return;
    const visibleWidth = 2 * this._worldHalfWidth;
    // eslint-disable-next-line no-console
    console.log(
      '[sand] tier=' + this._tier +
      ' activeColumns=' + this._columnCount +
      ' columnPitch(world units)=' + this._dx.toFixed(5) +
      ' visibleWidthAtPilePlane(world units)=' + visibleWidth.toFixed(5) +
      ' cameraDistance=' + CAMERA_DIST +
      ' fov=' + this._camera.fov + ' aspect=' + this._camera.aspect.toFixed(4)
    );
  }

  _buildBackground() {
    const fovRad = THREE.MathUtils.degToRad(this._camera.fov);
    const dist = CAMERA_DIST + 2.0;
    const h = 2 * dist * Math.tan(fovRad / 2) * 1.2;
    const w = h * this._camera.aspect;
    const geometry = new THREE.PlaneGeometry(w, h);
    this._bgUniforms = {
      uTop: { value: BG_TOP_COLOR.clone() },
      uBottom: { value: BG_BOTTOM_COLOR.clone() },
      uTime: { value: 0 }
    };
    const material = new THREE.ShaderMaterial({
      uniforms: this._bgUniforms,
      vertexShader: backgroundVertexShader(),
      fragmentShader: backgroundFragmentShader(),
      transparent: false,
      depthWrite: false,
      depthTest: false,
      side: THREE.FrontSide
    });
    this._bgGeometry = geometry;
    this._bgMesh = new THREE.Mesh(geometry, material);
    this._bgMesh.position.set(0, 0, -2.0);
    this._bgMesh.renderOrder = 0;
    this._scene.add(this._bgMesh);
  }

  // A single continuous ribbon: 2 vertices per column (base + top), the
  // top vertex sitting at this column's SMOOTHED height. X positions are
  // written once (and again on resize, see onResize) since column
  // positions never move; Y positions (the only thing that actually
  // changes frame to frame) are rewritten every frame in
  // _writeRenderAttributes.
  _buildPile() {
    const n = this._columnCount;
    const positions = new Float32Array(n * 2 * 3);
    const localY = new Float32Array(n * 2);
    const slopeAttr = new Float32Array(n * 2);
    for (let i = 0; i < n; i++) {
      localY[2 * i] = 0;
      localY[2 * i + 1] = 1;
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('aLocalY', new THREE.BufferAttribute(localY, 1));
    geometry.setAttribute('aSlope', new THREE.BufferAttribute(slopeAttr, 1));
    geometry.setIndex(buildRibbonIndices(n));
    geometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 0, 0), 40);

    this._pileUniforms = {
      uPileColor: { value: this._pileColorTo },
      uEmissionSideSign: { value: 1 },
      uSlopeShading: { value: this._slopeShading ? 1 : 0 }
    };
    this._pileMaterial = new THREE.ShaderMaterial({
      uniforms: this._pileUniforms,
      vertexShader: pileVertexShader(),
      fragmentShader: pileFragmentShader(),
      transparent: false,
      depthWrite: true,
      depthTest: true,
      side: THREE.DoubleSide
    });
    this._pileGeometry = geometry;
    this._pileMesh = new THREE.Mesh(geometry, this._pileMaterial);
    this._pileMesh.frustumCulled = false;
    this._pileMesh.renderOrder = 1;
    this._scene.add(this._pileMesh);

    this._writeRibbonX(this._pileGeometry);
  }

  // Same topology as the pile ribbon (see buildRibbonIndices), riding the
  // same smoothed height line but offset down by a fixed screen-pixel
  // thickness - real geometry, so it follows a continuous crest line
  // because its own vertices already sit on that line, rather than trying
  // to fake a rim inside each column's own fragment shader.
  _buildCrest() {
    const n = this._columnCount;
    const positions = new Float32Array(n * 2 * 3);
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setIndex(buildRibbonIndices(n));
    geometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 0, 0), 40);

    this._crestUniforms = Object.assign({}, safeZoneUniforms, {
      uCrestColor: { value: this._crestColorTo },
      uCrestBright: { value: 1 }
    });
    this._crestMaterial = new THREE.ShaderMaterial({
      uniforms: this._crestUniforms,
      vertexShader: crestVertexShader(),
      fragmentShader: crestFragmentShader(),
      transparent: true,
      depthWrite: false,
      depthTest: true,
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide
    });
    this._crestGeometry = geometry;
    this._crestMesh = new THREE.Mesh(geometry, this._crestMaterial);
    this._crestMesh.frustumCulled = false;
    this._crestMesh.renderOrder = 2;
    this._scene.add(this._crestMesh);

    this._writeRibbonX(this._crestGeometry);
  }

  _writeRibbonX(geometry) {
    const arr = geometry.attributes.position.array;
    for (let i = 0; i < this._columnCount; i++) {
      const x = -this._worldHalfWidth + (i + 0.5) * this._dx;
      arr[(2 * i) * 3] = x;
      arr[(2 * i + 1) * 3] = x;
    }
    geometry.attributes.position.needsUpdate = true;
  }

  _buildNet() {
    const geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(this._netPointCount * 3);
    const seeds = new Float32Array(this._netPointCount);
    for (let i = 0; i < this._netPointCount; i++) seeds[i] = i / this._netPointCount;
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('aSeed', new THREE.BufferAttribute(seeds, 1));
    geometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 0, 0), 40);

    // One full fall (emission height down to the floor) sets the cycle
    // length; grains near the pile's current surface simply vanish sooner
    // (see vVisible in the vertex shader) - nothing about the cycle itself
    // needs to change as the pile grows.
    this._netCycleDuration = (this._emissionY - this._floorY) / NET_FALL_SPEED;

    // One texel per column, R channel only (G/B unused, A left opaque) -
    // same RGBA-encoded-Uint8 convention as the height fields this file's
    // texture, sized to THIS tier's column count so u in the shader maps
    // 1:1 onto the active columns.
    this._netHeightTexData = new Uint8Array(this._columnCount * 4);
    this._netHeightTexData.fill(255); // alpha
    this._netHeightTex = new THREE.DataTexture(
      this._netHeightTexData, this._columnCount, 1, THREE.RGBAFormat);
    this._netHeightTex.minFilter = THREE.LinearFilter;
    this._netHeightTex.magFilter = THREE.LinearFilter;
    this._netHeightTex.wrapS = THREE.ClampToEdgeWrapping;
    this._netHeightTex.wrapT = THREE.ClampToEdgeWrapping;
    this._netHeightTex.needsUpdate = true;

    this._netUniforms = Object.assign({}, safeZoneUniforms, {
      uTime: { value: 0 },
      uCycleDuration: { value: this._netCycleDuration },
      uEmissionX: { value: 0 },
      uEmissionY: { value: this._emissionY },
      uHeightTex: { value: this._netHeightTex },
      uHeightTexMax: { value: NET_HEIGHT_TEX_MAX },
      uWorldHalfWidth: { value: this._worldHalfWidth },
      uFloorY: { value: this._floorY },
      uDispersion: { value: NET_DISPERSION_BASE },
      uFallSpeed: { value: NET_FALL_SPEED },
      uRefDist: { value: CAMERA_DIST },
      uDprScale: { value: 1 },
      uPointSizeMax: { value: Math.max(1, getMaxPointSize()) },
      uNetColor: { value: this._crestColorTo },
      uNetBright: { value: 1 }
    });
    this._netMaterial = new THREE.ShaderMaterial({
      uniforms: this._netUniforms,
      vertexShader: netVertexShader(),
      fragmentShader: netFragmentShader(),
      transparent: true,
      depthWrite: false,
      depthTest: true,
      blending: THREE.AdditiveBlending
    });
    this._netGeometry = geometry;
    this._netPoints = new THREE.Points(geometry, this._netMaterial);
    this._netPoints.frustumCulled = false;
    this._netPoints.renderOrder = 3;
    this._scene.add(this._netPoints);
  }

  // ?sandheight=1 - the raw (unsmoothed) height profile as a line, so an
  // avalanche propagating correctly (a smooth cascade, not isolated
  // spikes) is easy to confirm independently of the rendered pile's own
  // smoothing (AC1).
  _buildHeightDebug() {
    const positions = new Float32Array(this._columnCount * 3);
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    const material = new THREE.LineBasicMaterial({ color: 0x00ff88 });
    this._heightDebugGeometry = geometry;
    this._heightDebugLine = new THREE.Line(geometry, material);
    this._heightDebugLine.position.z = 1.0;
    this._heightDebugLine.renderOrder = 4;
    this._scene.add(this._heightDebugLine);
  }

  _updatePaletteColors(profile) {
    const palette = profile.artworkPalette;
    const dominant = palette && palette.dominantColors;
    const vivid = palette && palette.vividColors;
    const ref = dominant && dominant.length > 0 ? dominant[0] : null;
    if (ref !== this._lastPaletteRef) {
      this._lastPaletteRef = ref;
      this._pileColorFrom.copy(this._pileColorTo);
      this._crestColorFrom.copy(this._crestColorTo);

      const pileTarget = PILE_COLOR.clone();
      if (ref) pileTarget.lerp(new THREE.Color(ref), PALETTE_TINT_AMOUNT);
      this._pileColorToTarget = pileTarget;

      const crestTarget = new THREE.Color(vivid && vivid.length ? vivid[0] : FALLBACK_VIVID);
      this._crestColorToTarget = crestTarget;

      this._paletteLerpStart = this._time;
    }
    const t = clamp01((this._time - this._paletteLerpStart) / 0.9);
    if (this._pileColorToTarget) this._pileColorTo.copy(this._pileColorFrom).lerp(this._pileColorToTarget, t);
    if (this._crestColorToTarget) this._crestColorTo.copy(this._crestColorFrom).lerp(this._crestColorToTarget, t);
  }

  _simulateStep(dt, flowRate, maxSlopeHeight, sinkRatePerS) {
    // 1. deposit - spread over 6 columns, gaussian-ish weights, at a
    // position jittered +-DEPOSIT_JITTER_COLUMNS around the (already
    // drifting) emission point, redrawn every step - without this every
    // grain lands at the exact same spot and the summit is a mathematical
    // point, not a pile.
    const colF = (this._emissionX + this._worldHalfWidth) / this._dx
      + (Math.random() * 2 - 1) * DEPOSIT_JITTER_COLUMNS;
    depositGaussian(this._columnCount, colF, flowRate * dt);

    // 2. avalanche relaxation - RELAX_ITERATIONS passes, alternating sweep
    // direction once per SIMULATION STEP (not per inner iteration).
    for (let iter = 0; iter < RELAX_ITERATIONS; iter++) {
      relaxOnce(this._columnCount, maxSlopeHeight, this._sweepForward);
    }
    this._sweepForward = !this._sweepForward;

    // 3. sinking floor.
    if (sinkRatePerS > 0) {
      const factor = Math.max(0, 1 - sinkRatePerS * dt);
      for (let i = 0; i < this._columnCount; i++) height[i] *= factor;
    }
  }

  update(deltaTime, profile) {
    const paused = profile.playbackState !== 'PLAYING';
    this._paused = paused;
    if (paused) return; // freezes the simulation AND the time accumulator together

    const dt = Math.min(deltaTime, DT_CLAMP);
    this._time += dt;

    if (profile.trackId !== this._lastTrackId) {
      this._lastTrackId = profile.trackId;
      if (this._lastTrackId !== undefined) this._trackChangeSinkT = TRACK_CHANGE_SINK_DURATION_S;
    }

    this._updatePaletteColors(profile);

    const director = profile.director;
    const bands = (director && director.bands) || null;
    const hasGrid = !!(director && director.hasReliableGrid && bands);
    const character = (director && director.character) || null;
    const dynamics = character && character.dynamics != null ? character.dynamics : 0.5;
    const dynScale = lerp(DYNAMICS_SWING_MIN, DYNAMICS_SWING_MAX, dynamics);

    let bandsLow, bandsMid, bandsHigh;
    if (hasGrid) {
      bandsLow = bands.low; bandsMid = bands.mid; bandsHigh = bands.high;
    } else {
      bandsLow = 0.5 + 0.5 * Math.sin((2 * Math.PI * this._time) / this._fallbackPeriod);
      bandsMid = 0.5; bandsHigh = 0.5;
    }

    const flowMul = lerp(FLOW_BAND_MIN_MUL, FLOW_BAND_MAX_MUL, clamp01(bandsLow));
    // dynamics scales the DEVIATION of that swing from neutral (1x), same
    // convention as murmuration.js's own cohesion weight.
    const flowRate = this._forcedFlow != null
      ? this._forcedFlow
      : FLOW_RATE_BASE * (1 + (flowMul - 1) * dynScale);

    const slopeDeg = this._forcedSlopeDeg != null ? this._forcedSlopeDeg : ANGLE_OF_REPOSE_DEG;
    const maxSlopeHeight = Math.tan(THREE.MathUtils.degToRad(slopeDeg)) * this._dx;

    // Emission drift - slow, incommensurable-enough on its own since it is
    // the only periodic driver of its kind in this file.
    this._emissionX = Math.sin((2 * Math.PI * this._time) / EMISSION_DRIFT_PERIOD_S) * this._worldHalfWidth * EMISSION_DRIFT_FRAC;

    // Sinking floor rate - engaged once the pile presses the ceiling,
    // modulated by progress (slowest at the middle of the track - "culmine
    // au coeur du morceau"), and overridden by a fast, temporary sink right
    // after a track change ("le tas s'enfonce presque entierement").
    let sinkRatePerS = 0;
    if (this._trackChangeSinkT > 0) {
      sinkRatePerS = TRACK_CHANGE_SINK_RATE_PER_S;
      this._trackChangeSinkT = Math.max(0, this._trackChangeSinkT - dt);
    } else {
      const maxH = computeMaxHeight(this._columnCount);
      if (maxH > SINK_THRESHOLD_FRAC * this._worldHeight) {
        const progress = profile.duration > 0 ? clamp01(profile.progress) : 0.5;
        const progressMul = lerp(SINK_RATE_PROGRESS_MAX_MUL, SINK_RATE_PROGRESS_MIN_MUL, Math.sin(Math.PI * progress));
        sinkRatePerS = BASE_SINK_RATE_PER_S * progressMul;
      }
    }

    // Fixed-step integration - see this file's own header for why.
    this._accumulator += dt;
    let substeps = 0;
    while (this._accumulator >= FIXED_DT && substeps < MAX_SUBSTEPS) {
      this._simulateStep(FIXED_DT, flowRate, maxSlopeHeight, sinkRatePerS);
      this._accumulator -= FIXED_DT;
      substeps++;
    }

    // Render-only slope, once per frame regardless of how many substeps
    // ran - see computeSlopes's own comment.
    computeSlopes(this._columnCount, this._dx);

    // --- uniforms / render attributes ---
    const crestBright = lerp(CREST_BAND_MIN_MUL, CREST_BAND_MAX_MUL, clamp01(bandsHigh));
    this._crestUniforms.uCrestBright.value = crestBright * CREST_LUMINANCE;
    this._pileUniforms.uEmissionSideSign.value = this._emissionX >= 0 ? 1 : -1;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const canvasPixelHeight = window.innerHeight * dpr;
    const worldPerPx = this._worldHeight / canvasPixelHeight;
    this._crestWorldThickness = CREST_PX * worldPerPx;

    this._netUniforms.uDprScale.value = dpr;
    this._netUniforms.uTime.value = this._time;
    this._netUniforms.uEmissionX.value = this._emissionX;
    this._netUniforms.uDispersion.value = NET_DISPERSION_BASE * (1 + (clamp01(bandsMid) - 0.5) * 2 * NET_DISPERSION_BAND_SWING * dynScale);
    this._netUniforms.uNetBright.value = crestBright;

    this._bgUniforms.uTime.value = this._time;

    this._writeRenderAttributes();
    if (this._debugHeight) this._updateHeightDebug();
  }

  _writeRenderAttributes() {
    const n = this._columnCount;

    const pilePos = this._pileGeometry.attributes.position.array;
    const pileSlope = this._pileGeometry.attributes.aSlope.array;
    const crestPos = this._crestGeometry.attributes.position.array;
    const texData = this._netHeightTexData;
    const crestThickness = this._crestWorldThickness;

    for (let i = 0; i < n; i++) {
      const topY = this._floorY + Math.max(height[i], 0.0001);
      const bi = 2 * i, ti = 2 * i + 1;

      // X is written once (see _writeRibbonX) and never touched here.
      pilePos[bi * 3 + 1] = this._floorY;
      pilePos[ti * 3 + 1] = topY;
      pileSlope[bi] = slope[i];
      pileSlope[ti] = slope[i];

      crestPos[bi * 3 + 1] = this._floorY + Math.max(height[i] - crestThickness, 0);
      crestPos[ti * 3 + 1] = topY;

      texData[i * 4] = Math.round(clamp01(height[i] / NET_HEIGHT_TEX_MAX) * 255);
    }

    this._pileGeometry.attributes.position.needsUpdate = true;
    this._pileGeometry.attributes.aSlope.needsUpdate = true;
    this._crestGeometry.attributes.position.needsUpdate = true;
    this._netHeightTex.needsUpdate = true;
  }

  _updateHeightDebug() {
    const arr = this._heightDebugGeometry.attributes.position.array;
    for (let i = 0; i < this._columnCount; i++) {
      arr[i * 3] = -this._worldHalfWidth + (i + 0.5) * this._dx;
      arr[i * 3 + 1] = this._floorY + height[i];
      arr[i * 3 + 2] = 0;
    }
    this._heightDebugGeometry.attributes.position.needsUpdate = true;
    this._heightDebugGeometry.setDrawRange(0, this._columnCount);
  }

  onResize() {
    if (!this._camera || !this._pileMesh) return;
    this._recomputeWorld();
    this._writeRibbonX(this._pileGeometry);
    this._writeRibbonX(this._crestGeometry);

    this._bgMesh.geometry.dispose();
    const fovRad = THREE.MathUtils.degToRad(this._camera.fov);
    const dist = CAMERA_DIST + 2.0;
    const h = 2 * dist * Math.tan(fovRad / 2) * 1.2;
    const w = h * this._camera.aspect;
    this._bgMesh.geometry = new THREE.PlaneGeometry(w, h);

    this._netUniforms.uEmissionY.value = this._emissionY;
    this._netUniforms.uWorldHalfWidth.value = this._worldHalfWidth;
    this._netUniforms.uFloorY.value = this._floorY;
    this._netCycleDuration = (this._emissionY - this._floorY) / NET_FALL_SPEED;
    this._netUniforms.uCycleDuration.value = this._netCycleDuration;

    this._logSandInfo();
  }

  dispose() {
    this._scene.remove(this._bgMesh);
    this._bgGeometry.dispose();
    this._bgMesh.material.dispose();
    this._scene.remove(this._pileMesh);
    this._pileGeometry.dispose();
    this._pileMaterial.dispose();
    this._scene.remove(this._crestMesh);
    this._crestGeometry.dispose();
    this._crestMaterial.dispose();
    this._scene.remove(this._netPoints);
    this._netGeometry.dispose();
    this._netMaterial.dispose();
    this._netHeightTex.dispose();
    if (this._heightDebugLine) {
      this._scene.remove(this._heightDebugLine);
      this._heightDebugGeometry.dispose();
      this._heightDebugLine.material.dispose();
    }
  }
}
